import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Upload, FileText, Trash2, Download, Eye, Image as ImageIcon, GripVertical, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useDropzone } from "react-dropzone";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { format } from "date-fns";

interface CustomerMeasurementUploadProps {
  customerId: string;
  currentFileUrl?: string | null;
  currentData?: any;
  currentSource?: string | null;
  uploadedAt?: string | null;
  notes?: string | null;
  measurementPdfUrl?: string | null;
  projectPhotos?: any[];
  onUploadSuccess: () => void;
}

export const CustomerMeasurementUpload = ({
  customerId,
  currentFileUrl,
  currentData,
  currentSource,
  uploadedAt,
  notes,
  measurementPdfUrl,
  projectPhotos = [],
  onUploadSuccess,
}: CustomerMeasurementUploadProps) => {
  const [uploading, setUploading] = useState(false);
  const [localNotes, setLocalNotes] = useState(notes || "");
  const [previewData, setPreviewData] = useState<any>(null);
  const [photos, setPhotos] = useState<any[]>(projectPhotos || []);
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);
  const [editingCaption, setEditingCaption] = useState("");

  const onDropMeasurement = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);

    try {
      const fileExt = file.name.split(".").pop()?.toLowerCase();
      const fileName = `${customerId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("customer-measurements")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("customer-measurements")
        .getPublicUrl(filePath);

      let parsedData, parseError;

      if (fileExt === "xml") {
        const fileContent = await file.text();
        const result = await supabase.functions.invoke("parse-gaf-xml", {
          body: { xmlContent: fileContent }
        });
        parsedData = result.data;
        parseError = result.error;
      } else if (fileExt === "dxf") {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('scaleFactor', '1');
        
        const result = await supabase.functions.invoke("parse-dxf", {
          body: formData
        });
        parsedData = result.data;
        parseError = result.error;
      } else {
        throw new Error(`Unsupported file type: ${fileExt}`);
      }

      if (parseError) throw parseError;
      if (!parsedData?.success) {
        throw new Error(parsedData?.error || 'Failed to parse file');
      }

      const measurementData = parsedData?.data || parsedData;

      const { error: updateError } = await supabase
        .from("customers")
        .update({
          measurement_file_url: publicUrl,
          measurement_data: measurementData,
          measurement_source: fileExt === "xml" ? "xml" : "dxf",
          measurement_uploaded_at: new Date().toISOString(),
          measurement_notes: localNotes || null,
        })
        .eq("id", customerId);

      if (updateError) throw updateError;

      setPreviewData(measurementData);
      toast({ title: `${fileExt?.toUpperCase()} file uploaded and parsed successfully` });
      onUploadSuccess();
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const onDropPdf = async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setUploading(true);

    try {
      const fileName = `${customerId}-measurement-${Date.now()}.pdf`;
      const filePath = `${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("customer-measurements")
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("customer-measurements")
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("customers")
        .update({ measurement_pdf_url: publicUrl })
        .eq("id", customerId);

      if (updateError) throw updateError;

      toast({ title: "PDF uploaded successfully" });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const onDropPhotos = async (acceptedFiles: File[]) => {
    setUploading(true);

    try {
      const uploadedPhotos = [];

      for (const file of acceptedFiles) {
        const fileExt = file.name.split(".").pop()?.toLowerCase();
        const fileName = `${customerId}-photo-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
        const filePath = `${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from("customer-measurements")
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from("customer-measurements")
          .getPublicUrl(filePath);

        uploadedPhotos.push({
          id: crypto.randomUUID(),
          url: publicUrl,
          caption: "",
          uploaded_at: new Date().toISOString(),
          display_order: photos.length + uploadedPhotos.length,
          include_in_proposal: true,
        });
      }

      const updatedPhotos = [...photos, ...uploadedPhotos];
      setPhotos(updatedPhotos);

      const { error: updateError } = await supabase
        .from("customers")
        .update({ project_photos: updatedPhotos })
        .eq("id", customerId);

      if (updateError) throw updateError;

      toast({ title: `${uploadedPhotos.length} photo(s) uploaded successfully` });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps: getMeasurementRootProps, getInputProps: getMeasurementInputProps, isDragActive: isMeasurementDragActive } = useDropzone({
    onDrop: onDropMeasurement,
    accept: {
      "application/xml": [".xml"],
      "application/dxf": [".dxf"],
      "application/acad": [".dxf"],
    },
    multiple: false,
    disabled: uploading,
  });

  const { getRootProps: getPdfRootProps, getInputProps: getPdfInputProps, isDragActive: isPdfDragActive } = useDropzone({
    onDrop: onDropPdf,
    accept: { "application/pdf": [".pdf"] },
    multiple: false,
    disabled: uploading,
  });

  const { getRootProps: getPhotosRootProps, getInputProps: getPhotosInputProps, isDragActive: isPhotosDragActive } = useDropzone({
    onDrop: onDropPhotos,
    accept: { "image/*": [".jpg", ".jpeg", ".png", ".webp"] },
    multiple: true,
    disabled: uploading,
  });

  const handleDeleteMeasurement = async () => {
    if (!confirm("Are you sure you want to delete this measurement file?")) return;

    try {
      const { error } = await supabase
        .from("customers")
        .update({
          measurement_file_url: null,
          measurement_data: null,
          measurement_source: null,
          measurement_uploaded_at: null,
          measurement_notes: null,
        })
        .eq("id", customerId);

      if (error) throw error;

      toast({ title: "Measurement deleted successfully" });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to delete measurement",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeletePdf = async () => {
    if (!confirm("Are you sure you want to delete this PDF?")) return;

    try {
      const { error } = await supabase
        .from("customers")
        .update({ measurement_pdf_url: null })
        .eq("id", customerId);

      if (error) throw error;

      toast({ title: "PDF deleted successfully" });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to delete PDF",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    if (!confirm("Delete this photo?")) return;

    const updatedPhotos = photos.filter(p => p.id !== photoId);
    setPhotos(updatedPhotos);

    try {
      const { error } = await supabase
        .from("customers")
        .update({ project_photos: updatedPhotos })
        .eq("id", customerId);

      if (error) throw error;

      toast({ title: "Photo deleted" });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to delete photo",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleUpdatePhotoCaption = async (photoId: string, caption: string) => {
    const updatedPhotos = photos.map(p => 
      p.id === photoId ? { ...p, caption } : p
    );
    setPhotos(updatedPhotos);

    try {
      const { error } = await supabase
        .from("customers")
        .update({ project_photos: updatedPhotos })
        .eq("id", customerId);

      if (error) throw error;

      setEditingPhotoId(null);
      toast({ title: "Caption updated" });
    } catch (error: any) {
      toast({
        title: "Failed to update caption",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleToggleIncludeInProposal = async (photoId: string) => {
    const updatedPhotos = photos.map(p => 
      p.id === photoId ? { ...p, include_in_proposal: !p.include_in_proposal } : p
    );
    setPhotos(updatedPhotos);

    try {
      const { error } = await supabase
        .from("customers")
        .update({ project_photos: updatedPhotos })
        .eq("id", customerId);

      if (error) throw error;
    } catch (error: any) {
      toast({
        title: "Failed to update photo",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleSaveNotes = async () => {
    try {
      const { error } = await supabase
        .from("customers")
        .update({ measurement_notes: localNotes || null })
        .eq("id", customerId);

      if (error) throw error;

      toast({ title: "Notes saved" });
      onUploadSuccess();
    } catch (error: any) {
      toast({
        title: "Failed to save notes",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Measurements & Files
        </CardTitle>
        <CardDescription>
          Manage measurement files, PDFs, and roof photos for this customer
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="measurements" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="measurements">
              Measurements
              {currentFileUrl && <Badge variant="secondary" className="ml-2">✓</Badge>}
            </TabsTrigger>
            <TabsTrigger value="pdf">
              PDF Report
              {measurementPdfUrl && <Badge variant="secondary" className="ml-2">✓</Badge>}
            </TabsTrigger>
            <TabsTrigger value="photos">
              Roof Photos
              {photos.length > 0 && <Badge variant="secondary" className="ml-2">{photos.length}</Badge>}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="measurements" className="space-y-4">
            {currentFileUrl ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="font-medium">Measurement File</span>
                      {currentSource && (
                        <Badge variant="outline" className="uppercase">
                          {currentSource}
                        </Badge>
                      )}
                    </div>
                    {uploadedAt && (
                      <p className="text-sm text-muted-foreground">
                        Uploaded {format(new Date(uploadedAt), "PPp")}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPreviewData(currentData)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={currentFileUrl} download>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </a>
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleDeleteMeasurement}>
                      <Trash2 className="h-4 w-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>

                {previewData && (
                  <div className="p-4 bg-muted rounded-lg">
                    <h4 className="font-medium mb-2">Measurement Data Preview</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {previewData.area_sqft && (
                        <div>
                          <span className="text-muted-foreground">Area:</span>{" "}
                          <span className="font-medium">{previewData.area_sqft} sq ft</span>
                        </div>
                      )}
                      {previewData.pitch && (
                        <div>
                          <span className="text-muted-foreground">Pitch:</span>{" "}
                          <span className="font-medium">{previewData.pitch}</span>
                        </div>
                      )}
                      {previewData.ridge_lf && (
                        <div>
                          <span className="text-muted-foreground">Ridge:</span>{" "}
                          <span className="font-medium">{previewData.ridge_lf} LF</span>
                        </div>
                      )}
                      {previewData.eave_lf && (
                        <div>
                          <span className="text-muted-foreground">Eave:</span>{" "}
                          <span className="font-medium">{previewData.eave_lf} LF</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes">Measurement Notes</Label>
                  <Textarea
                    id="notes"
                    value={localNotes}
                    onChange={(e) => setLocalNotes(e.target.value)}
                    placeholder="Add notes about these measurements..."
                    rows={3}
                  />
                  <Button onClick={handleSaveNotes} size="sm">
                    Save Notes
                  </Button>
                </div>
              </div>
            ) : (
              <div
                {...getMeasurementRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isMeasurementDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
                } ${uploading ? "opacity-50 cursor-not-allowed" : "hover:border-primary"}`}
              >
                <input {...getMeasurementInputProps()} />
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                {uploading ? (
                  <p className="text-lg font-medium">Uploading...</p>
                ) : (
                  <>
                    <p className="text-lg font-medium mb-2">Drag & drop XML or DXF file</p>
                    <p className="text-sm text-muted-foreground">or click to browse</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="pdf" className="space-y-4">
            {measurementPdfUrl ? (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="font-medium">GAF QuickMeasure Report</span>
                  </div>
                  <p className="text-sm text-muted-foreground">PDF with aerial images</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <a href={measurementPdfUrl} download target="_blank" rel="noopener noreferrer">
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </a>
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDeletePdf}>
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div
                {...getPdfRootProps()}
                className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                  isPdfDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
                } ${uploading ? "opacity-50 cursor-not-allowed" : "hover:border-primary"}`}
              >
                <input {...getPdfInputProps()} />
                <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                {uploading ? (
                  <p className="text-lg font-medium">Uploading...</p>
                ) : (
                  <>
                    <p className="text-lg font-medium mb-2">Drag & drop PDF report</p>
                    <p className="text-sm text-muted-foreground">GAF QuickMeasure or similar</p>
                  </>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="photos" className="space-y-4">
            <div
              {...getPhotosRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                isPhotosDragActive ? "border-primary bg-primary/5" : "border-muted-foreground/25"
              } ${uploading ? "opacity-50 cursor-not-allowed" : "hover:border-primary"}`}
            >
              <input {...getPhotosInputProps()} />
              <ImageIcon className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              {uploading ? (
                <p className="font-medium">Uploading...</p>
              ) : (
                <>
                  <p className="font-medium mb-1">Upload roof photos</p>
                  <p className="text-xs text-muted-foreground">Drag & drop or click to browse</p>
                </>
              )}
            </div>

            {photos.length > 0 && (
              <div className="grid grid-cols-2 gap-4">
                {photos.map((photo) => (
                  <Card key={photo.id} className="overflow-hidden">
                    <CardContent className="p-0">
                      <div className="aspect-video relative">
                        <img
                          src={photo.url}
                          alt={photo.caption || "Roof photo"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="p-3 space-y-2">
                        {editingPhotoId === photo.id ? (
                          <div className="flex gap-2">
                            <Input
                              value={editingCaption}
                              onChange={(e) => setEditingCaption(e.target.value)}
                              placeholder="Add caption..."
                              className="text-sm"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleUpdatePhotoCaption(photo.id, editingCaption)}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <p
                            className="text-sm text-muted-foreground cursor-pointer hover:text-foreground"
                            onClick={() => {
                              setEditingPhotoId(photo.id);
                              setEditingCaption(photo.caption || "");
                            }}
                          >
                            {photo.caption || "Click to add caption..."}
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              checked={photo.include_in_proposal !== false}
                              onCheckedChange={() => handleToggleIncludeInProposal(photo.id)}
                            />
                            <span className="text-xs text-muted-foreground">Include in proposal</span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeletePhoto(photo.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
